import { useEffect, useRef, useState } from "react";

/**
 * Lightweight scroll-reveal wrapper.
 * - Uses IntersectionObserver so nothing polls or reads scroll position.
 * - Reveal is a fade + tiny (12px) upward transform + subtle scale into place.
 * - Reveals ONCE per element (unobserved after first intersection) so scrolling
 *   past never triggers another animation and never affects the page's scroll
 *   position (transform is purely visual).
 * - Respects prefers-reduced-motion — the child renders immediately, no animation.
 * - No scrollIntoView, no layout shift: the placeholder occupies its natural
 *   space at all times.
 */
export default function Reveal({ as: Tag = "div", delayMs = 0, children, className = "", ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setVisible(true); return; }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const t = setTimeout(() => setVisible(true), delayMs);
            io.disconnect();
            return () => clearTimeout(t);
          }
        }
      },
      { root: null, rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delayMs]);

  return (
    <Tag
      ref={ref}
      data-reveal={visible ? "in" : "out"}
      className={`${className} transition-[opacity,transform] duration-700 ease-out will-change-transform`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0px) scale(1)" : "translateY(12px) scale(0.985)",
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
